import { orderByMetadata } from "./chemistryMetadata";
import { ChemistryEditorMaterials } from "./types";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  SearchChemicalElements,
  SearchMaterialChemistry,
} from "contexts/search/context";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useLanguage } from "hooks/settings";
import { useMemo, useState } from "react";
import { parseChemistryMeasurement } from "./chemistryMeasurements";
import { MaterialChemistry, SupportedLanguage } from "store/api/generatedApi";

interface AddMaterialChemistryFormProps {
  materials: ChemistryEditorMaterials;
  chemicalElements: SearchChemicalElements;
  materialChemistry: SearchMaterialChemistry;
  materialChemistryOverrides: MaterialChemistry[];
  onAdd: (
    materialId: number,
    chemicalElementId: number,
    loc: number,
    scale: number
  ) => void;
  showScale: boolean;
}

export const AddMaterialChemistryForm = ({
  materials,
  chemicalElements,
  materialChemistry,
  materialChemistryOverrides,
  onAdd,
  showScale,
}: AddMaterialChemistryFormProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const language = useLanguage();
  const notifyStatus = useNotifyStatus();

  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(
    null
  );
  const [selectedChemicalElementId, setSelectedChemicalElementId] = useState<
    number | null
  >(null);
  const [locText, setLocText] = useState("");
  const [scaleText, setScaleText] = useState("");

  const combinationExists = useMemo(() => {
    if (selectedMaterialId === null || selectedChemicalElementId === null) {
      return false;
    }

    const existsInContext =
      materialChemistry[selectedMaterialId]?.[selectedChemicalElementId];

    const existsInOverrides = materialChemistryOverrides.some(
      (override) =>
        override.material_id === selectedMaterialId &&
        override.chemical_element_id === selectedChemicalElementId
    );

    return !!existsInContext || existsInOverrides;
  }, [
    selectedMaterialId,
    selectedChemicalElementId,
    materialChemistry,
    materialChemistryOverrides,
  ]);

  const handleSubmit = () => {
    if (selectedMaterialId === null || selectedChemicalElementId === null) {
      notifyStatus({
        text: t("pleaseSelectMaterialAndElement"),
        type: "error",
      });
      return;
    }

    if (combinationExists) {
      notifyStatus({
        text: t("combinationAlreadyExists"),
        type: "error",
      });
      return;
    }

    try {
      const loc = parseChemistryMeasurement({
        text: locText,
        language: language as SupportedLanguage,
      });
      const scale = showScale
        ? parseChemistryMeasurement({
            text: scaleText,
            language: language as SupportedLanguage,
          })
        : 0;

      onAdd(selectedMaterialId, selectedChemicalElementId, loc, scale);

      setSelectedMaterialId(null);
      setSelectedChemicalElementId(null);
      setLocText("");
      setScaleText("");

      notifyStatus({
        text: t("materialChemistryAdded"),
        type: "success",
      });
    } catch {
      notifyStatus({ text: t("invalidNumber"), type: "error" });
    }
  };

  const handleReset = () => {
    setSelectedMaterialId(null);
    setSelectedChemicalElementId(null);
    setLocText("");
    setScaleText("");
  };

  const isFormValid =
    selectedMaterialId !== null &&
    selectedChemicalElementId !== null &&
    !combinationExists;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        border: 1,
        borderColor: theme.palette.divider,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="h6" gutterBottom>
        {t("addNewMaterialChemistry")}
      </Typography>

      <FormControl size="small" fullWidth>
        <InputLabel id="material-select-label">{t("material")}</InputLabel>
        <Select<number | "">
          labelId="material-select-label"
          value={selectedMaterialId ?? ""}
          label={t("material")}
          onChange={(event) => {
            if (event.target.value === "") {
              setSelectedMaterialId(null);
            } else {
              setSelectedMaterialId(event.target.value);
            }
          }}
        >
          <MenuItem value="">
            <em>{t("selectMaterial")}</em>
          </MenuItem>
          {orderByMetadata(materials.byIndex).map((material) => (
            <MenuItem key={material.id} value={material.id}>
              {material.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel id="element-select-label">{t("element")}</InputLabel>
        <Select<number | "">
          labelId="element-select-label"
          value={selectedChemicalElementId ?? ""}
          label={t("element")}
          onChange={(event) => {
            if (event.target.value === "") {
              setSelectedChemicalElementId(null);
            } else {
              setSelectedChemicalElementId(event.target.value);
            }
          }}
        >
          <MenuItem value="">
            <em>{t("selectElement")}</em>
          </MenuItem>
          {orderByMetadata(chemicalElements.byIndex).map((element) => (
            <MenuItem key={element.id} value={element.id}>
              {element.symbol} ({t(element.name)})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {combinationExists ? (
        <Typography
          variant="body2"
          color="warning.main"
          sx={{ fontStyle: "italic", textAlign: "center" }}
        >
          {t("combinationAlreadyExists")}
        </Typography>
      ) : null}

      <TextField
        size="small"
        fullWidth
        label={showScale ? t("loc") : t("value")}
        value={locText}
        onChange={(event) => {
          setLocText(event.target.value);
        }}
        placeholder="0.0000"
        disabled={combinationExists}
        sx={{
          "& input": {
            textAlign: "right",
            ...theme.typography.body1Mono,
          },
        }}
      />

      {showScale ? (
        <TextField
          size="small"
          fullWidth
          label={t("scale")}
          value={scaleText}
          onChange={(event) => {
            setScaleText(event.target.value);
          }}
          placeholder="0.0000"
          disabled={combinationExists}
          sx={{
            "& input": {
              textAlign: "right",
              ...theme.typography.body1Mono,
            },
          }}
        />
      ) : null}

      <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={handleReset} size="small">
          {t("reset")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid}
          size="small"
        >
          {t("add")}
        </Button>
      </Box>
    </Box>
  );
};
